import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { generateConfig } from "../../utils/api";
import { handleApiError } from "../../utils/apiErrorHandler";

// Create a new progress summary
export const createProgressSummary = createAsyncThunk(
  "progressSummary/create",
  async (progressSummaryData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Progress Summary Thunk] Creating progress summary for client: ${progressSummaryData.clientName}`);

      const response = await axios.post(
        `${baseURL}/api/progress-summary`,
        progressSummaryData,
        config
      );

      console.log(`[Progress Summary Thunk] Successfully created progress summary:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Progress Summary Thunk] Error creating progress summary:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch all progress summaries
export const fetchProgressSummaries = createAsyncThunk(
  "progressSummary/fetchAll",
  async (params = {}, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Progress Summary Thunk] Fetching progress summaries with params:`, params);

      const queryParams = new URLSearchParams();
      if (params.clientId) queryParams.append('clientId', params.clientId);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const response = await axios.get(
        `${baseURL}/api/progress-summary?${queryParams.toString()}`,
        config
      );

      console.log(`[Progress Summary Thunk] Successfully fetched ${response.data.docs?.length || 0} progress summaries`);
      return response.data;
    } catch (error) {
      console.error(`[Progress Summary Thunk] Error fetching progress summaries:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch a specific progress summary by ID
export const fetchProgressSummaryById = createAsyncThunk(
  "progressSummary/fetchById",
  async (progressSummaryId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Progress Summary Thunk] Fetching progress summary: ${progressSummaryId}`);

      const response = await axios.get(
        `${baseURL}/api/progress-summary/${progressSummaryId}`,
        config
      );

      console.log(`[Progress Summary Thunk] Successfully fetched progress summary:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Progress Summary Thunk] Error fetching progress summary:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Update a progress summary
export const updateProgressSummary = createAsyncThunk(
  "progressSummary/update",
  async ({ progressSummaryId, progressSummaryData }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Progress Summary Thunk] Updating progress summary: ${progressSummaryId}`);

      const response = await axios.put(
        `${baseURL}/api/progress-summary/${progressSummaryId}`,
        progressSummaryData,
        config
      );

      console.log(`[Progress Summary Thunk] Successfully updated progress summary:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Progress Summary Thunk] Error updating progress summary:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Delete a progress summary
export const deleteProgressSummary = createAsyncThunk(
  "progressSummary/delete",
  async (progressSummaryId, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Progress Summary Thunk] Deleting progress summary: ${progressSummaryId}`);

      const response = await axios.delete(
        `${baseURL}/api/progress-summary/${progressSummaryId}`,
        config
      );

      console.log(`[Progress Summary Thunk] Successfully deleted progress summary`);
      return { progressSummaryId, message: response.data.message };
    } catch (error) {
      console.error(`[Progress Summary Thunk] Error deleting progress summary:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch progress summaries for a specific client
export const fetchClientProgressSummaries = createAsyncThunk(
  "progressSummary/fetchByClient",
  async ({ clientId, page = 1, limit = 10 }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Progress Summary Thunk] Fetching progress summaries for client: ${clientId}`);

      const response = await axios.get(
        `${baseURL}/api/progress-summary/client/${clientId}?page=${page}&limit=${limit}`,
        config
      );

      console.log(`[Progress Summary Thunk] Successfully fetched client progress summaries:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`[Progress Summary Thunk] Error fetching client progress summaries:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Autosave progress summary (create or update as draft)
export const autosaveProgressSummary = createAsyncThunk(
  "progressSummary/autosave",
  async ({ progressSummaryId, progressSummaryData }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      // Add draft status and autosave flag
      const dataWithDraftStatus = {
        ...progressSummaryData,
        status: 'draft',
        isAutosave: true
      };

      let response;

      if (progressSummaryId) {
        // Update existing draft
        console.log(`[Progress Summary Thunk] Autosaving existing progress summary: ${progressSummaryId}`);
        response = await axios.put(
          `${baseURL}/api/progress-summary/${progressSummaryId}`,
          dataWithDraftStatus,
          config
        );
      } else {
        // Create new draft
        console.log(`[Progress Summary Thunk] Creating new draft progress summary`);
        response = await axios.post(
          `${baseURL}/api/progress-summary`,
          dataWithDraftStatus,
          config
        );
      }

      console.log(`[Progress Summary Thunk] Autosave successful`);
      return response.data;
    } catch (error) {
      console.error(`[Progress Summary Thunk] Error autosaving progress summary:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);

// Fetch user's draft progress summaries
export const fetchMyDraftProgressSummaries = createAsyncThunk(
  "progressSummary/fetchMyDrafts",
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const token = state.auth.token;
      const { baseURL, config } = generateConfig({ token });

      console.log(`[Progress Summary Thunk] Fetching user's draft progress summaries`);

      const response = await axios.get(
        `${baseURL}/api/progress-summary/drafts/my`,
        config
      );

      console.log(`[Progress Summary Thunk] Found ${response.data.count} drafts`);
      return response.data;
    } catch (error) {
      console.error(`[Progress Summary Thunk] Error fetching drafts:`, error);
      return handleApiError(error, rejectWithValue, dispatch);
    }
  }
);